import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaCloudUploadAlt, FaTrash, FaSpinner, FaExpand, FaColumns, FaTh } from 'react-icons/fa';
import { uploadEstimateImage, deleteEstimateImage, updateEstimateImage, getEstimateImages } from './EstimateService';
import FeedbackModal from '../../components/shared/ModalNotification';
import { compressImage } from '../../utils/imageCompression';

const API_URL = process.env.REACT_APP_API_URL || '';

// Matches the multer limit on the API and client_max_body_size in nginx.
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

const Container = styled.div`
  margin-top: ${props => props.theme.spacing(3)};
`;

const DropZone = styled.div`
  border: 2px dashed ${props => props.isDragging ? props.theme.colors.primary : props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(4)};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.isDragging ? props.theme.colors.primary + '11' : props.theme.colors.surface};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.primary}11;
  }
`;

const DropZoneIcon = styled.div`
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const DropZoneText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
  margin: 0;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing(2)};
  margin-top: ${props => props.theme.spacing(2)};
`;

const ImageCard = styled.div`
  position: relative;
  border-radius: ${props => props.theme.borderRadius};
  overflow: hidden;
  background: ${props => props.theme.colors.surface};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
  display: block;
`;

const ImageControls = styled.div`
  padding: ${props => props.theme.spacing(1.5)};
`;

const ImageName = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const SizeSelector = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const SizeButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 4px;
  border: 1px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.inputGray};
  background: ${props => props.active ? props.theme.colors.primary + '22' : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  border-radius: 4px;
  cursor: pointer;
  font-size: 10px;
  font-weight: ${props => props.active ? '600' : '400'};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }

  svg { font-size: 12px; }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(220, 53, 69, 0.9);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: transform 0.15s;

  &:hover { transform: scale(1.1); }
`;

const UploadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;

  svg { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const SizeLabel = styled.div`
  font-size: 10px;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 4px;
  font-weight: 500;
`;

const EstimateImageUpload = ({ estimateId }) => {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [feedback, setFeedback] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (estimateId) {
      loadImages();
    }
  }, [estimateId]);

  const loadImages = async () => {
    try {
      const response = await getEstimateImages(estimateId);
      setImages(response.data);
    } catch (error) {
      console.error('Error loading images:', error);
    }
  };

  const uploadFiles = async (files) => {
    if (!files.length || !estimateId) return;
    setUploading(true);
    const failed = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const counter = files.length > 1 ? ` (${i + 1}/${files.length})` : '';
      try {
        // Shrink phone-camera photos before they are validated or sent.
        setStatusText(`Preparing ${file.name}...${counter}`);
        const prepared = await compressImage(file);
        if (prepared.size > MAX_UPLOAD_BYTES) {
          throw new Error(`${file.name} is still over the 25MB limit after compression`);
        }

        setStatusText(`Uploading ${prepared.name}...${counter}`);
        const response = await uploadEstimateImage(estimateId, prepared, 'full');
        setImages(prev => [...prev, response.data]);
      } catch (error) {
        console.error('Error uploading file:', file.name, error);
        failed.push(file.name);
      }
    }

    setStatusText('');
    setUploading(false);

    if (failed.length) {
      setFeedback({
        success: false,
        message: failed.length === 1
          ? `Could not upload ${failed[0]}.`
          : `Could not upload ${failed.length} images.`,
      });
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    uploadFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      uploadFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDelete = async (imageId) => {
    try {
      await deleteEstimateImage(estimateId, imageId);
      setImages(prev => prev.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const handleSizeChange = async (imageId, displaySize) => {
    try {
      await updateEstimateImage(estimateId, imageId, { displaySize });
      setImages(prev => prev.map(img =>
        img.id === imageId ? { ...img, displaySize } : img
      ));
    } catch (error) {
      console.error('Error updating image:', error);
    }
  };

  const getImageUrl = (filename) => {
    const baseUrl = API_URL.replace(/\/api$/, '');
    return `${baseUrl}/estimate-images/${filename}`;
  };

  if (!estimateId) {
    return (
      <Container>
        <DropZoneText>Save the estimate first to upload images.</DropZoneText>
      </Container>
    );
  }

  return (
    <Container>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      <DropZone
        isDragging={isDragging}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <DropZoneIcon>
          {uploading ? <FaSpinner className="icon-spin" /> : <FaCloudUploadAlt />}
        </DropZoneIcon>
        <DropZoneText>
          {uploading
            ? (statusText || 'Uploading...')
            : 'Drop images here or click to browse'}
        </DropZoneText>
      </DropZone>

      {images.length > 0 && (
        <ImageGrid>
          {images.map((image) => (
            <ImageCard key={image.id}>
              <ImagePreview
                src={getImageUrl(image.filename)}
                alt={image.originalName}
              />
              <DeleteButton onClick={() => handleDelete(image.id)}>
                <FaTrash />
              </DeleteButton>
              <ImageControls>
                <ImageName title={image.originalName}>{image.originalName}</ImageName>
                <SizeLabel>PDF Layout:</SizeLabel>
                <SizeSelector>
                  <SizeButton
                    active={image.displaySize === 'full'}
                    onClick={() => handleSizeChange(image.id, 'full')}
                    title="Full width"
                  >
                    <FaExpand /> Full
                  </SizeButton>
                  <SizeButton
                    active={image.displaySize === 'half'}
                    onClick={() => handleSizeChange(image.id, 'half')}
                    title="2 columns"
                  >
                    <FaColumns /> 1/2
                  </SizeButton>
                  <SizeButton
                    active={image.displaySize === 'third'}
                    onClick={() => handleSizeChange(image.id, 'third')}
                    title="3 columns"
                  >
                    <FaTh /> 1/3
                  </SizeButton>
                </SizeSelector>
              </ImageControls>
            </ImageCard>
          ))}
        </ImageGrid>
      )}

      <FeedbackModal
        isOpen={!!feedback}
        success={feedback?.success}
        message={feedback?.message || ''}
        duration={3000}
        onComplete={() => setFeedback(null)}
      />
    </Container>
  );
};

export default EstimateImageUpload;
